'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ShopProductCard } from '@/app/[locale]/[username]/(shop)/components/ProductCard';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { SendATEvent } from '@/modules/activityTracker/SendATEvent';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useCurrentRoute } from '@/modules/activityTracker/useCurrentRoute';
import { type CurrencyCode } from '@/modules/currencies/constants';
import {
  buildProductImageProps,
  picturePreviewAdapter,
} from '@/modules/pictures/helpers';
import {
  SIMILAR_TO_SOLD_PRODUCTS_COUNT,
  SIMILAR_TO_SOLD_REQUIRED_PRODUCTS_COUNT,
} from '@/modules/similar/constants';
import { useSimilarToSoldProducts } from '@/modules/similar/hooks/useSimilarToSoldProducts';

type Props = {
  soldProductIds: number[];
  requiredProductsCount?: number;
};

export function SimilarToSold({
  soldProductIds,
  requiredProductsCount = SIMILAR_TO_SOLD_REQUIRED_PRODUCTS_COUNT,
}: Props) {
  const tCommon = useTranslations('common');
  const {
    data: products,
    isLoading,
    isSuccess,
  } = useSimilarToSoldProducts(soldProductIds);
  const currentRoute = useCurrentRoute();
  const productsToRender =
    products?.slice(0, SIMILAR_TO_SOLD_PRODUCTS_COUNT) || [];
  const hasRequiredProductsCount =
    soldProductIds.length > 0 &&
    productsToRender.length >= requiredProductsCount;

  const isProductsVisible = isSuccess && hasRequiredProductsCount;
  const shouldRender = isLoading || isProductsVisible;

  if (!shouldRender) {
    return null;
  }

  return (
    <section className={styles.container}>
      {isProductsVisible && (
        <SendATEvent
          event={ActivityTrackerEventType.MODULE_VIEW}
          params={{
            module: 'SimilarToSold',
            page: currentRoute,
          }}
        />
      )}
      <Body variant="sm-bold" className={styles.headingText}>
        {tCommon('YouMightAlsoLike')}
      </Body>
      <div className={styles.productGrid}>
        {isLoading
          ? Array.from({ length: SIMILAR_TO_SOLD_PRODUCTS_COUNT }).map(
              (_, index) => (
                <Placeholder
                  className={styles.skeletonCard}
                  key={index}
                  shape={PlaceHolderShape.RECTANGLE}
                  width="100%"
                  height="100%"
                />
              )
            )
          : productsToRender.map((product) => (
              <ShopProductCard
                key={product.id}
                slug={product.slug}
                preview={picturePreviewAdapter(product.pictures?.[0])}
                status={product.status}
                price={{
                  currencyName: product.pricing.currency as CurrencyCode,
                  priceAmount: product.pricing.original_price.total_price,
                  discountedPriceAmount:
                    product.pricing.discounted_price?.total_price,
                }}
                displayPrice={product.pricing.display_price}
                HoverOverlay={() => (
                  <LazyLoadImage
                    {...buildProductImageProps({
                      preview: picturePreviewAdapter(product.pictures?.[1]),
                    })}
                  />
                )}
              />
            ))}
      </div>
    </section>
  );
}
